import {
  ChartAssetData,
  CustomMixDomain,
  getPrimaryDetails,
  PaneData,
  RangeHorizon,
  ResampleIntervals,
} from '@toggle/chart';
import { useState } from 'react';
import { useSearchParams } from 'react-router';

import {
  buildChartSearchParams,
  ValidChartSettings,
} from '../../utils/chart-settings-utils/chart-settings-utils';
import { emptyChartState } from '../../utils/chart-utils/chart-utils';
import { ChartDataResult } from '../use-chart-settings/useChartSettings';

export interface ChartState {
  chartPanes: PaneData[];
  resample: ResampleIntervals;
  horizon: RangeHorizon | null;
  domain: CustomMixDomain | null;
  hidden?: string[];
  chartChanged: boolean;
}

export type UseChartStateReturn = ReturnType<typeof useChartState>;

export const useChartState = (shouldUseSearchParams: boolean) => {
  const [initialChartState, setInitialChartState] = useState<ChartState>();
  const [chartState, setChartState] = useState<ChartState>(
    () => emptyChartState
  );
  const [primaryAsset, setPrimaryAsset] = useState<ChartAssetData>();
  const [primaryPane, setPrimaryPane] = useState<PaneData>();
  const [, setSearchParams] = useSearchParams();

  const updateChartState = (partial: Partial<ChartState>) => {
    const nextChartState = { ...chartState, ...partial };

    setChartState(nextChartState);

    if (!initialChartState) {
      setInitialChartState(
        structuredClone({
          ...nextChartState,
          // Required to trigger the useEffect in the useChartApi() hook
          chartPanes: [...nextChartState.chartPanes],
        })
      );
      setChartState(prev => ({ ...prev, chartChanged: false }));
    }

    if (partial.chartPanes) {
      const { primaryAsset, primaryPane } = getPrimaryDetails(
        partial.chartPanes
      );
      setPrimaryAsset(primaryAsset);
      setPrimaryPane(primaryPane);
    }

    if (shouldUseSearchParams) {
      const urlSearchParams = buildChartSearchParams({
        searchParams: new URLSearchParams(location.search),
        primaryAsset,
        ...partial,
      });
      setSearchParams(urlSearchParams, { replace: true });
    }
  };

  const resetChartState = async (
    loadChartFromSettings: (
      settings: ValidChartSettings
    ) => Promise<ChartDataResult>
  ) => {
    if (initialChartState) {
      const assets = chartState.chartPanes
        .map((pane, id) =>
          pane.chartAssetData.map(data => ({
            tag: data.entity.tag,
            snake: data.snakeMeta?.snake || '',
            paneIndex: id,
          }))
        )
        .flat();

      const chartData = await loadChartFromSettings({
        domain: initialChartState.domain,
        resample: initialChartState.resample,
        horizon: initialChartState.horizon || undefined,
        assets: assets,
        hidden: initialChartState.hidden,
        paneSettings: {},
      });

      setChartState({
        ...initialChartState,
        chartPanes: chartData.chartPanes,
        chartChanged: false,
      });
    }
  };

  return {
    updateChartState,
    resetChartState,
    chartState,
    initialChartState,
    setInitialChartState,
    primaryAsset,
    primaryPane,
    setChartState,
  };
};
